<template>
  <!-- Photo and words (split) in its five layouts (S_Variants). -->
  <SectionBand :variant="variant" :background="section.background" label="Photo and words">
    <div class="s2-split" :data-layout="layout">
      <div class="s2-split__media mo-reveal">
        <div v-if="layout === 'strip'" class="s2-split__strip">
          <Media v-for="(img, i) in stripImages" :key="i" :media="img" ratio="3 / 4" />
        </div>
        <Media v-else :media="section.image" :ratio="layout === 'arch' ? '3 / 4' : '4 / 3'" class="s2-split__photo" />
      </div>
      <div class="s2-split__words mo-reveal" :class="{'t-card': layout === 'overlap'}">
        <component :is="headingTag" v-if="section.title" class="t-h2">{{ section.title }}</component>
        <div class="s2-prose t-body" v-html="html" />
        <a v-if="section.callToAction && href" :href="back(href)" class="t-btn s2-split__btn">{{ section.callToAction }}</a>
      </div>
    </div>
  </SectionBand>
</template>

<script setup>
import {useSectionHeadingTag} from "@/composables/useSectionHeadingTag.js";
import {computed} from "vue";
import SectionBand from "../shared/SectionBand.vue";
import Media from "../shared/Media.vue";
import {contentHtml} from "@/utils/contentHtml.js";
import {safeLink, stripPhotos} from "@/utils/blocks.js";
import {useBackLinks} from "@/composables/useBackLinks.js";
const back = useBackLinks();
const headingTag = useSectionHeadingTag();

const props = defineProps({section: {type: Object, required: true}, variant: {type: [Number, String], default: 1}});
const LAYOUTS = ["left", "right", "overlap", "strip", "arch"];
// No layout saved: today's textLeft decides which side the photo goes.
const layout = computed(() => (LAYOUTS.includes(props.section.layout) ? props.section.layout : (props.section.textLeft === false ? "left" : "right")));
const html = computed(() => contentHtml(props.section.content || ""));
const href = computed(() => safeLink(props.section.link || props.section.navigateTo || ""));
const stripImages = computed(() => stripPhotos(props.section));
</script>

<style scoped>
.s2-split { display: grid; gap: clamp(1.5rem, 4vw, 3.5rem); align-items: center; grid-template-columns: 1fr 1fr; }
.s2-split[data-layout="right"] .s2-split__media { order: 2; }
.s2-split[data-layout="arch"] { grid-template-columns: .8fr 1fr; }
.s2-split[data-layout="arch"] .s2-split__photo :deep(img) { border-radius: 999px 999px var(--t-radius) var(--t-radius); }
.s2-split[data-layout="overlap"] { grid-template-columns: 1.2fr .8fr; }
.s2-split[data-layout="overlap"] .s2-split__words { margin-left: -18%; position: relative; z-index: 1; }
.s2-split[data-layout="strip"] { grid-template-columns: 1fr; }
.s2-split[data-layout="strip"] .s2-split__media { order: 2; }
.s2-split__strip { display: grid; grid-template-columns: repeat(3, 1fr); gap: var(--t-gap); }
.s2-split__words .t-h2 { margin-bottom: 1rem; }
.s2-split__btn { margin-top: 1.5rem; }
.s2-prose :deep(p) { margin: 0 0 .9em; }
@media (max-width: 760px) {
  .s2-split, .s2-split[data-layout="arch"], .s2-split[data-layout="overlap"] { grid-template-columns: 1fr; }
  .s2-split[data-layout="right"] .s2-split__media { order: 0; }
  .s2-split[data-layout="overlap"] .s2-split__words { margin: -3rem 1rem 0; }
}
</style>
